<template>
  <div class="form-horizontal">
    <!-- URL Field Start -->
    <Field
      :label="$t('HTTP_URL')"
      :has-tooltip="true"
      :tooltip-text="$t('HTTP_URL_TT')"
    >
      <template #element>
        <ValidationProvider
          v-slot="{ classes }"
          ref="httpRequestUrl"
          rules="required|url"
          slim
        >
          <div :class="classes">
            <div class="col-xs-10 col-sm-6">
              <HttpUrl
                v-model="requestSettings.url"
                name="httpRequestUrl"
                :enable-multi-line="enableMultiLineUrl"
                @input="updateRequestSettings"
              />             
            </div>
            <label 
              v-t="'REQUIRED'"
              class="col-xs-2 col-sm-3 form-group__label-required"
            />
          </div>
        </ValidationProvider>
      </template>
    </Field>
    <!-- URL Field End -->
    <!-- HTTP Method Field Start -->
    <Field
      :label="$t('HTTP_METHOD')"
      :has-tooltip="true"
      :tooltip-text="$t('HTTP_METHOD_TT')"
    >
      <template #element>
        <div class="col-sm-3 col-md-2">
          <HttpMethodSelector
            v-model="requestSettings.method"
            name="httpRequestMethod"
            :custom-options="httpConnectionOptions"
            @input="updateRequestSettingsMethod"
          />
        </div>
      </template>
    </Field>
    <!-- HTTP Method Field End -->
    <!-- Authentication Field Start -->
    <Field
      v-if="hasAuth"
      :label="$t('HTTP_AUTHENTICATION')"
      :has-tooltip="true"
      :tooltip-text="$t('HTTP_AUTHENTICATION_TT')"
    >
      <template #element>
        <div class="col-sm-3">
          <SearchableDropdown
            v-model="requestSettings.authentication"
            :config="authenticationOptions"
            :searchable="true"
            :clearable="true"
            class="m-0 r-form__searchable-dropdown"
            @change="updateRequestSettings"
          />
        </div>
      </template>
    </Field>
    <!-- Authentication Field End -->
    <!-- Body Field Start -->
    <Field
      v-if="showRequestBody"
      :label="$t('HTTP_BODY')"
      :has-tooltip="true"
      :tooltip-text="$t('HTTP_BODY_TT')"
    >
      <template #element>
        <ValidationProvider
          v-slot="{ classes }"
          ref="httpRequestBody"
          rules="required"
          slim
        >
          <div :class="classes">
            <div class="col-xs-10 col-sm-6">
              <HttpBody
                v-model="requestSettings.body"
                :has-macros="true"
                body-type="json"
                name="httpRequestBody"
                custom-class="no-radius"
                @input="updateRequestSettings"
              />
              <label
                v-if="!isValidJson(requestSettings.body)"
                class="m-t-5 red"
                v-t="'ERROR_INVALID_JSON'"
              />
            </div>                   
            <label 
              v-t="'REQUIRED'"
              class="col-xs-2 col-sm-3 form-group__label-required"
            />
          </div>
        </ValidationProvider>
      </template>
    </Field>
    <!-- Body Field End -->
    <!--Headers Field Start -->
    <Field
      :label="$t('HTTP_HEADERS')"
      :has-tooltip="true"
      :tooltip-text="$t('HTTP_HEADERS_TT')"
    >
      <template #element>
        <ValidationProvider
          v-slot="{ classes }"
          ref="httpRequestHeaders"
          slim
        >
          <div :class="classes">
            <div class="col-sm-8 col-md-6">
              <HttpHeaders
                v-model="requestSettings.headers"
                name="httpRequestHeaders"
                :enable-macros="enableMacros"
                @input="updateRequestSettings"
              />
            </div>
          </div>
        </ValidationProvider>
      </template>
    </Field>
    <!-- Headers Field End -->

    <div class="row m-t-20">
      <HttpRequestSampleTokens
        v-if="selectedMacros && selectedMacros.length"
        :selected-macros.sync="selectedMacros"
        class="col-sm-offset-3 col-sm-8 col-md-6 m-b-15"
      />
    </div>
    <div class="row m-b-15">
      <div class="col-md-offset-3 col-md-9">
        <!-- <button
          class="btn btn-sm btn-danger m-r-10"
          type="button"
        >
          {{ $t('BUTTON_REVERT') }}
        </button> -->
        <button
          class="btn"
          type="button"
          @click="runHttpRequest"
          :disabled="!isRequestReady"
        >
          {{ $t('BUTTON_RUN_REQUEST') }}
          <i aria-hidden="true" class="fa fa-play" />
        </button>
        <Loader
          v-if="requestStatusLoading"
          custom-class="size-sm m-l-5"
        />
        <i v-if="requestStatusSuccess" class="fa fa-check text-success m-l-10" aria-hidden="true" />
        <i v-if="requestStatusFail" class="fa fa-warning text-danger m-l-10" aria-hidden="true" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import { STATUS_SUCCESS, STATUS_PENDING, STATUS_ERROR } from '@/store/helpers';
import { httpConnectionOptions } from '../helpers/constants';
import { buildMacros } from '../helpers';
import { isValidJson } from '../helpers/validation';
import sharedMessages from '@/core/locales';
import HttpUrl from '../HttpUrl';
import HttpMethodSelector from '../HttpMethodSelector';
import HttpBody from '../HttpBody';
import HttpHeaders from '../HttpHeaders';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import HttpRequestSampleTokens from './HttpRequestSampleTokens';

const authenticationOptionsConfig = {
  reduce: (option) => option.id,
  label: 'name',
  placeholder: "Choose Authentication"
};

export default {
  name: 'HttpRequest',
  components: {
    HttpUrl,
    HttpMethodSelector,
    HttpBody,
    HttpHeaders,
    Field,
    SearchableDropdown,
    HttpRequestSampleTokens
  },
  props: {
    requestSettings: {
      type: Object,
      default: () => ({
        url: '',
        method: 'get',
        body: '',
        authentication: null,
        headers: {},
        contentType: 'application/json'
      })
    },
    hasAuth: {
      type: Boolean,
      default: false
    },
    requestStatus: {
      type: String,
      default: ''
    },
    enableMultiLineUrl: {
      type: Boolean,
      default: false
    },
    enableMacros: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedMacros: [],
      isHttpRequestUrlValid: false
    };
  },
  computed: {
    ...mapState('authConnections', ['authConnections']),
    authenticationOptions() {
      return {
        options: this.authConnections,
        ...authenticationOptionsConfig,
        placeholder: this.$t('AUTHENTICATION_PLACEHOLDER')
      };
    },
    isRequestReady() {
      if (this.showRequestBody) {
        return this.isHttpRequestUrlValid && this.requestSettings.body && this.isSampleMacrosValid && this.isValidJson(this.requestSettings.body);
      }
      return this.isHttpRequestUrlValid && this.isSampleMacrosValid;
    },
    isSampleMacrosValid() {
      if (this.selectedMacros?.length) {
        return this.selectedMacros.every(macro => macro.hasOwnProperty('value') && macro['value']);
      }
      return true;
    },
    httpRequestUrl() {
      if (this.selectedMacros && this.selectedMacros.length) {
        let requestUrl = this.requestSettings.url;
        this.selectedMacros.forEach(({ name, value }) => {
          requestUrl = requestUrl.replaceAll(name, value);
        });
        return requestUrl;
      }
      return this.requestSettings.url;
    },
    httpRequestBody() {
      if (this.requestSettings.body) {
        if (this.selectedMacros && this.selectedMacros.length) {
          let requestBody = this.requestSettings.body;
          this.selectedMacros.forEach(({ name, value }) => {
            requestBody = requestBody.replaceAll(name, value);
          });
          if (this.isValidJson(requestBody)) {
            return JSON.parse(requestBody);
          }
          return requestBody;
        }
        if (this.isValidJson(this.requestSettings.body)) {
          return JSON.parse(this.requestSettings.body);
        }
        return this.requestSettings.body;
      }
      return '';
    },
    showRequestBody() {
      return this.requestSettings.method === 'post' || this.requestSettings.method === 'put' || this.requestSettings.method === 'patch';
    },
    requestStatusLoading() {
      return this.requestStatus == STATUS_PENDING;
    },
    requestStatusSuccess() {
      return this.requestStatus == STATUS_SUCCESS;
    },
    requestStatusFail() {
      return this.requestStatus == STATUS_ERROR;
    },
  },
  watch: {
    'requestSettings.url': {
      immediate: false,
      async handler(newValue) {
        this.processMacros(newValue, this.requestSettings.body);
      }
    },
    'requestSettings.body': {
      immediate: false,
      async handler(newValue) {
        this.processMacros(this.requestSettings.url, newValue);
      }
    },
  },
  created() {
    this.httpConnectionOptions = httpConnectionOptions;
    if (this.hasAuth) this.getAuthConnections();
  },
  methods: {
    ...mapActions('authConnections', ['getAuthConnections']),
    isValidJson,
    async runHttpRequest() {
      const payload = {
        requestSettings: this.requestSettings,
        completedRequestUrl: this.httpRequestUrl,
        completedRequestBody: this.httpRequestBody ? this.httpRequestBody : ''
      }
      this.$emit('runHttpRequest', payload);
    },
    updateRequestSettingsMethod() {
      if (this.requestSettings.method === 'get') this.requestSettings.body = '';
      this.processMacros(this.requestSettings.url, this.requestSettings.body);
      this.updateRequestSettings();

      this.$emit('updateRequestSettingsMethod', this.requestSettings.method);
    },
    updateRequestSettings() {
      this.$emit('updateRequestSettings', this.requestSettings);
    },
    async processMacros(url, body) {
      this.isHttpRequestUrlValid = await this.validateHttpRequestUrl();
      this.selectedMacros = buildMacros(url, body, this.selectedMacros);
    },
    async validateHttpRequestUrl() {
      await this.$nextTick();
      if (this.$refs.httpRequestUrl) {
        const response = await this.$refs.httpRequestUrl.validate();
        return response.valid;
      }
      return false;
    },
  },
  i18n: {
    sharedMessages,
    locale: 'en'
  } 
};
</script>